import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { prettyJsonStringify } from 'webapp/utils/common';
import { getHeightWithMargins } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import onWindowEvents from 'webapp/modifiers/on-window-events';
import BarLoaderComponent from 'webapp/components/bar-loader';
import FormCodeEditorComponent from 'webapp/components/form/code-editor';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';

interface Args {
	form: CustomMetricSetupFormApiMetricBuilder;
	theme: string;
	isLoading: boolean;
	isDisabled: boolean;
	selectedRowIndex?: number;
	onRowSelect?: (rowIndex: number) => void;
	maxHeightExtraReservedSpace?: number; // reduce maxHeight by this amount
	onMaxHeightChange?: (maxHeight: number) => void; // to possibly inform expression component
}

export default class CustomQuerySetupApiMetricBuilderRawResponseComponent extends Component<Args> {
	@tracked isValid = true;
	@tracked maxHeight: number | null = null;

	get rawDataString(): string {
		const rawData = this.args.form.rawData;
		return rawData ? prettyJsonStringify(rawData) : '';
	}

	get canSelectRows(): boolean {
		return !!this.args.onRowSelect;
	}

	get rowOptions(): LabelValuePairNumeric[] {
		return (this.args.form.jsonStructure?.rows || []).map((row) => {
			return {
				value: row.index,
				label: '',
				disabled: !row.isValid,
			};
		});
	}

	get editorWrapperStyle(): SafeString {
		const styleString = this.maxHeight ? `height:${this.maxHeight}px` : '';
		return htmlSafe(styleString);
	}

	@action
	onTextChange(): void {
		// skip, is read-only
	}

	@action
	onRowChange(rowIndex: AnySimpleValue): void {
		if (this.args.isDisabled) {
			return;
		}

		if (!this.canSelectRows) {
			return;
		}

		if (this.args.onRowSelect) {
			this.args.onRowSelect(rowIndex as number);
		}
	}

	@action
	onScroll(pos: number): void {
		// sync radiobuttons with code editor
		const radiobuttonsNode = document.querySelectorAll('.editor-wrapper .radiobutton-group')[0];
		if (radiobuttonsNode) {
			radiobuttonsNode.scrollTop = pos;
		}
	}

	@action
	updateMaxEditorContainerHeight(_e: Event | null, node: HTMLElement): void {
		const wizardCt = node.closest<HTMLElement>('.custom-metric-wizard');

		if (!wizardCt) {
			// not wizard - skip
			return;
		}

		const contentCt = wizardCt.querySelector<HTMLElement>('.cmw-content'),
			controlsCt = wizardCt.querySelector<HTMLElement>('.cmw-controls'),
			stepCt = contentCt?.querySelector<HTMLElement>('.step-ct'),
			rowCt = node.parentNode as HTMLElement;

		if (!stepCt) {
			return;
		}

		const contentTextHeight = getHeightWithMargins(
				stepCt.querySelector<HTMLElement>(':scope > .top'),
			),
			contentControlsHeight = getHeightWithMargins(
				stepCt.querySelector<HTMLElement>(':scope > .controls'),
			),
			errorMessageHeight = getHeightWithMargins(
				stepCt.querySelector<HTMLElement>(':scope > .errorMessage'),
			),
			rowHeadingHeight = getHeightWithMargins(
				rowCt?.querySelector<HTMLElement>(':scope > strong'),
			),
			rowMarginHeight = getHeightWithMargins(rowCt) - rowCt.offsetHeight;

		let radiobuttonsHeight = 0;
		Array.from(stepCt?.querySelectorAll<HTMLElement>(':scope > .radio') || []).forEach((el) => {
			const h = getHeightWithMargins(el);
			if (h) {
				radiobuttonsHeight += h;
			}
		});

		const contentCtStyles = window.getComputedStyle(contentCt as Element),
			contentPaddingHeight =
				parseInt(contentCtStyles.paddingTop) + parseInt(contentCtStyles.paddingBottom),
			contentSpaceMax =
				wizardCt.offsetHeight - (controlsCt?.offsetHeight || 0) - contentPaddingHeight,
			contentSpaceTaken =
				contentTextHeight +
				contentControlsHeight +
				errorMessageHeight +
				radiobuttonsHeight +
				rowMarginHeight +
				rowHeadingHeight,
			extraReservedSpace = this.args.maxHeightExtraReservedSpace || 0;

		const maxHeight = Math.max(250, contentSpaceMax - contentSpaceTaken - extraReservedSpace);

		this.maxHeight = Math.round(maxHeight);
		if (this.args.onMaxHeightChange) {
			this.args.onMaxHeightChange(maxHeight);
		}
	}
	<template>
		<div
			class="row row--raw-response {{if @isLoading 'loading'}} {{if @isDisabled 'disabled'}}"
		>
			<strong>Response</strong>

			<div
				class="editor-wrapper"
				style={{this.editorWrapperStyle}}
				{{didInsert (fn this.updateMaxEditorContainerHeight null)}}
				{{onWindowEvents this.updateMaxEditorContainerHeight "resize" 150}}
			>

				{{#if this.canSelectRows}}
					<FormRadiobuttonGroupComponent
						class="radiobutton-default"
						@name="response-row-selector"
						@selected={{@selectedRowIndex}}
						@options={{this.rowOptions}}
						@onChange={{this.onRowChange}}
						@isDisabled={{@isDisabled}}
					/>
				{{/if}}

				<FormCodeEditorComponent
					@text={{this.rawDataString}}
					@mode="json"
					@isReadOnly={{true}}
					@onTextChange={{this.onTextChange}}
					@selectedRowIndex={{@selectedRowIndex}}
					@onRowChange={{this.onRowChange}}
					@onScroll={{this.onScroll}}
					@theme={{@theme}}
					@height={{this.maxHeight}}
				/>

			</div>

			{{#if @isLoading}}
				<div class="loader-ct">
					<BarLoaderComponent />
				</div>
			{{/if}}
		</div>
	</template>
}
